<template>
  <b-modal
    id="speaker-modal"
    size="lg"
    centered
    hide-header
    hide-footer
    @hidden="$emit('closed')"
  >
    <!-- Close button -->
    <button
      class="close-button"
      @click="$bvModal.hide('speaker-modal')"
      aria-label="Close"
    >
      <i class="material-icons">close</i>
    </button>
    <div v-if="speaker">
      <!-- Image -->
      <div class="d-flex justify-content-center mt-5">
        <img
          :src="speaker.profilePicture"
          :style="{
            borderColor: tags_getColor(speaker.sessionTags[0]),
          }"
          class="speaker-img"
          alt=""
        />
      </div>

      <!-- Name -->
      <h1 class="speaker-name text-center mt-3">
        {{ speaker.firstName }} {{ speaker.lastName }}
      </h1>

      <!-- Description -->
      <FadingSpeakerBio v-if="sessionId" class="mt-3" :bio="speaker.bio" />
      <p v-else class="mt-3">
        {{ speaker.bio }}
      </p>

      <!-- Session Details Header -->
      <h2 class="mt-4">Session details</h2>

      <!-- Session Items -->
      <div v-for="session in speaker.sessions" :key="session._id">
        <b-card no-body class="session-card">
          <b-card-header
            class="d-flex justify-content-between align-items-center"
          >
            <!-- Collapse toggle -->
            <div
              v-b-toggle="'session-' + session._id"
              class="d-flex align-items-center pointer w-100"
            >
              <span class="session-title mr-3">
                {{ session.title }}
              </span>
              <b-icon
                :icon="isOpen[session._id] ? 'chevron-up' : 'chevron-down'"
                class="ml-auto mr-4"
              />
            </div>
            <!-- Toast -->
            <Toast
              v-if="showingToastIndex === session._id"
              style="margin-top: -35px; margin-right: -15px"
              :message="
                schedule_includes(session._id)
                  ? 'Added to your schedule'
                  : 'Removed from your schedule'
              "
              :duration="2000"
              @hidden="showingToastIndex = null"
            />
            <!-- Add/Remove to/from schedule button -->
            <button
              variant="light"
              class="edit-schedule-button"
              @click="
                schedule_edit(session._id), (showingToastIndex = session._id)
              "
            >
              <b-icon
                :icon="
                  schedule_includes(session._id)
                    ? 'dash-circle-fill'
                    : 'plus-circle-fill'
                "
                class="schedule-button"
                :class="
                  schedule_includes(session._id)
                    ? 'remove-schedule-button'
                    : 'add-schedule-button'
                "
                font-scale="3.0"
              />
            </button>
          </b-card-header>

          <b-collapse
            :id="'session-' + session._id"
            :visible="isOpen[session._id]"
            @show="isOpen[session._id] = true"
            @hide="isOpen[session._id] = false"
          >
            <b-card-body>
              <b-row class="mb-3">
                <b-col>
                  <p class="session-description mb-0">
                    {{ session.description }}
                  </p>
                </b-col>
              </b-row>
              <b-row class="mb-3">
                <b-col>
                  <div class="session-info">
                    <SessionTag :tag="session.tag" />
                    <div class="info-tag">
                      <img
                        src="https://res.cloudinary.com/oredev/image/upload/v1716803684/2025/Icons/door.svg"
                      />
                      <span> Room: {{ session.roomName }} </span>
                    </div>
                    <div class="info-tag">
                      <img
                        src="https://res.cloudinary.com/oredev/image/upload/v1716803684/2025/Icons/clock.svg"
                      />
                      <span>
                        Time:
                        {{
                          ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
                            session.weekDay
                          ]
                        }}
                        {{ session.start }} - {{ session.end }} &rarr;
                        {{ session.duration }} min
                      </span>
                    </div>
                  </div>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <button
                    @click="schedule_edit(session._id)"
                    class="btn lg-schedule-button mb-4"
                    :class="
                      schedule_includes(session._id)
                        ? 'btn-orange'
                        : 'btn-lime-green'
                    "
                  >
                    {{
                      schedule_includes(session._id)
                        ? "Remove from schedule"
                        : "Add to schedule"
                    }}
                  </button>
                </b-col>
              </b-row>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
    </div>
  </b-modal>
</template>

<script>
import tags from "@/mixins/tags";
import schedule from "@/mixins/schedule";
import FadingSpeakerBio from "@/components/FadingSpeakerBio";
import SessionTag from "@/components/SessionTag";
import Toast from "@/components/Toast";

export default {
  name: "SpeakerModal",
  mixins: [tags, schedule],
  components: { FadingSpeakerBio, SessionTag, Toast },
  data() {
    return {
      isOpen: {},
      showingToastIndex: null,
    };
  },
  props: {
    speaker: Object,
    sessionId: String,
  },
  watch: {
    // Make the isOpen value reactive for the chevron
    speaker: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.sessions && newVal.sessions.length) {
          newVal.sessions.forEach((session) => {
            // set isOpen true if match with session, else false
            const isTarget = session._id === this.sessionId;
            this.$set(this.isOpen, session._id, isTarget);
          });
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  line-height: 100%;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: $dark-brown !important; // Override IOS color
  .material-icons {
    color: $dark-brown !important; // Override IOS color
  }
}

.speaker-img {
  width: 160px;
  border-radius: 50%;
  border: 7px solid;
  object-fit: cover;
}

h1,
h2,
span {
  font-family: Lexend, sans-serif;
}

.speaker-name {
  font-weight: 500;
  font-size: 20px;
  letter-spacing: 0.05rem;
  text-align: center;
  text-transform: none;
}

.speaker-bio {
}

h2 {
  font-weight: 500;
  font-size: 20px;
  letter-spacing: 0.05rem;
  text-transform: none;
}

.card {
  background-color: white;
  border: none;
  border-bottom: 1px solid #00000033;
  border-radius: 0;
}

.card-body {
  padding: 0;
}

.card-header {
  padding: 12px 0;
  border: none;
  background-color: white;
}

.session-title {
  font-weight: 400;
  font-size: 16px;
  letter-spacing: 0.05rem;
}

.edit-schedule-button {
  padding: 0;
  border: none;
  background: none;
  box-shadow: none;
  border-radius: 100%;
  .schedule-button {
    background-color: $dark-brown;
    border: 2px solid $dark-brown;
    width: 40px;
    height: 40px;
    border-radius: 100%;
    box-shadow: 0px 2px 4px 0px #00000040;
  }

  .add-schedule-button {
    color: $lime-green;
    &:hover {
      color: $dark-brown;
      background-color: $lime-green;
    }
  }
  .remove-schedule-button {
    color: $orange;
    &:hover {
      color: $dark-brown;
      background-color: $orange;
    }
  }
}

.session-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap; /* Allows wrapping if needed on small screens */
  gap: 8px; /* Adds spacing between spans */
  .info-tag {
    background-color: $light-grey;
    display: flex; /* Horizontal layout */
    align-items: center; /* Vertically center image + text */
    align-self: start;
    width: fit-content;

    border-radius: 5px;
    padding: 4px 8px;
    line-height: 100%;

    img {
      width: 16px;
      margin-right: 4px;
    }
    span {
      font-size: 14px;
      font-weight: 300;
    }
  }
}

.lg-schedule-button {
  letter-spacing: 0.05rem;
}
</style>


// WEBPACK FOOTER //
// src/components/modals/SpeakerModal.vue